import { useEffect, useState } from 'react';
import { benefitsApi } from '../../../api/benefits.js';

export default function EligibilityRules() {
  const [rules, setRules] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);

  function refresh() {
    setLoading(true);
    Promise.all([benefitsApi.listEligibilityRules(), benefitsApi.listPlans()])
      .then(([r, p]) => { setRules(r?.items || []); setPlans(p?.items || []); })
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(refresh, []);

  async function submit(e) {
    e.preventDefault();
    // Capture the form node before any await: React nulls `e.currentTarget`
    // once the event handler returns, so reading it post-await throws.
    const form = e.currentTarget;
    const fd = new FormData(form);
    const rule = {};
    if (fd.get('employment_type')) rule.employment_type = fd.get('employment_type');
    if (fd.get('min_tenure_days')) rule.min_tenure_days = Number(fd.get('min_tenure_days'));
    if (fd.get('location')) rule.location = fd.get('location');
    try {
      await benefitsApi.createEligibilityRule({
        plan_id: fd.get('plan_id'),
        rule,
        description: fd.get('description') || null,
      });
      form.reset();
      setShowForm(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create');
    }
  }

  async function del(id) {
    try {
      await benefitsApi.deleteEligibilityRule(id);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete');
    }
  }

  const planName = (id) => plans.find((p) => p.id === id)?.name || id;

  return (
    <section data-testid="benefits-eligibility-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Eligibility Rules</h1>
          <p className="muted">{rules.length} rules</p>
        </div>
        <button className="btn primary" data-testid="benefits-rule-create" onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Close' : '+ New Rule'}
        </button>
      </header>

      {error && <div className="alert error" data-testid="benefits-eligibility-error">{error}</div>}

      {showForm && (
        <form onSubmit={submit} className="card form-card grid-form">
          <label>Plan
            <select name="plan_id" data-testid="benefits-rule-plan-input" required>
              <option value="">— select —</option>
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.plan_year})</option>)}
            </select>
          </label>
          <label>Employment Type<input name="employment_type" data-testid="benefits-rule-emptype-input" placeholder="full_time" /></label>
          <label>Min Tenure (days)<input name="min_tenure_days" type="number" min="0" data-testid="benefits-rule-tenure-input" /></label>
          <label>Location<input name="location" data-testid="benefits-rule-location-input" /></label>
          <label className="full">Description<input name="description" data-testid="benefits-rule-desc-input" /></label>
          <div className="full actions">
            <button className="btn primary" type="submit" data-testid="benefits-rule-submit">Add Rule</button>
          </div>
        </form>
      )}

      <div className="card">
        <table className="data-table" data-testid="benefits-rule-list">
          <thead>
            <tr><th>Plan</th><th>Rule</th><th>Description</th><th></th></tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} data-testid={`benefits-rule-row-${r.id}`}>
                <td>{planName(r.plan_id)}</td>
                <td><code>{JSON.stringify(r.rule)}</code></td>
                <td>{r.description || '—'}</td>
                <td>
                  <button className="btn ghost small danger" data-testid={`benefits-rule-delete-${r.id}`} onClick={() => del(r.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {!loading && rules.length === 0 && (
              <tr><td colSpan={4} className="muted">No rules yet — all plans are open to all employees.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
